import { Plus, X } from '@phosphor-icons/react';
import { useState } from 'react';
import { formatNumber } from './format';
import { kitStyles as k } from './kit';
import { fromSi, type Quantity } from './units';
import v from './ValueLibrary.module.css';
import type { ReferenceValue } from '../model/types';
import { addReferenceValue, removeReferenceValue } from '../state/actions';
import { useStore } from '../state/store';

/**
 * The engineer's saved values for a field: click one to use it, or save the value in the field under
 * a name. Extent ships none of these; they come from the engineer's own copy of the standard or
 * their firm's practice, and travel with the project.
 */
export function ValueLibrary({
  kind, quantity, unit, current, onPick, hint,
}: {
  kind: ReferenceValue['kind'];
  quantity: Quantity;
  unit: string;
  /** The field's present value, offered for saving. */
  current: number | undefined;
  onPick: (si: number) => void;
  /** Where these values come from in the standard, cited by number. */
  hint: string;
}) {
  const values = useStore((s) => s.doc?.settings.referenceValues)?.filter((x) => x.kind === kind) ?? [];
  const [naming, setNaming] = useState(false);
  const [label, setLabel] = useState('');
  const show = (si: number) => `${formatNumber(fromSi(si, quantity, unit))} ${unit}`;

  const save = () => {
    if (current !== undefined && label.trim()) addReferenceValue(kind, label, current);
    setLabel('');
    setNaming(false);
  };

  return (
    <div className={v.wrap}>
      {values.length > 0 && (
        <ul className={v.chips}>
          {values.map((x) => (
            <li key={x.id}>
              <button type="button" className={v.chip} title={`Use ${show(x.valueSi)}`} onClick={() => onPick(x.valueSi)}>
                <span className={v.label}>{x.label}</span>
                <span className={v.value}>{show(x.valueSi)}</span>
              </button>
              <button type="button" className={v.remove} aria-label={`Remove ${x.label}`} title={`Remove ${x.label}`} onClick={() => removeReferenceValue(x.id)}>
                <X size={10} weight="bold" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {naming ? (
        <div className={v.naming}>
          <input
            autoFocus className={k.input} value={label} placeholder="Name this value, e.g. Flange, fibre gasket"
            aria-label="Name for the saved value"
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } if (e.key === 'Escape') { setLabel(''); setNaming(false); } }}
            onBlur={save}
          />
        </div>
      ) : (
        <div className={v.foot}>
          <button type="button" className={v.add} disabled={current === undefined} onClick={() => setNaming(true)}>
            <Plus size={11} weight="bold" /> Save {current !== undefined ? show(current) : 'this value'}
          </button>
          {values.length === 0 && <span className={v.hint}>{hint}</span>}
        </div>
      )}
    </div>
  );
}
